/**
 * Wizard state model and reducer (Task 10.1).
 *
 * Holds the full cross-step `WizardState` and the ordered list of steps that
 * back the progress indicator and Back/Next navigation (design wizard state
 * machine). The state mirrors `EstimateInput` from `@tcc/shared` plus UI-only
 * fields (the selected model, the entered volumetrics field, uploaded files).
 */

import type {
	ContentType,
	ContextDocumentRef,
	Currency,
	EstimateInput,
	ImageSizeTier,
	InteractionType,
	OutputSize,
	PricingTier,
} from '@tcc/shared';
import { deriveVolumetrics } from '@tcc/shared';

/** Agent deployment type for runtime cost estimation. */
export type AgentType = 'simpleChatbot' | 'standardAgentic' | 'complexMultiAgent' | 'heavyToolUse' | 'none';

/** The seven wizard steps plus the terminal results screen. */
export const STEP_IDS = [
	'contentType',
	'interactionType',
	'volumetrics',
	'dataUnits',
	'contextDocs',
	'decisionOutput',
	'results',
] as const;

export type StepId = (typeof STEP_IDS)[number];

/** Total number of INPUT steps shown in the progress indicator (excludes results). */
export const TOTAL_INPUT_STEPS = 6;

export interface WizardState {
	stepIndex: number;
	contentTypes: ContentType[];
	interactionType?: InteractionType;
	volumetrics: { perDay: number; perMonth: number; perYear: number };
	dataUnits: {
		textPages: number;
		voiceMinutes: number;
		imageCount: number;
		imageSize: ImageSizeTier;
		videoMinutes: number;
	};
	contextDocuments: ContextDocumentRef[];
	additionalContext: { enabled: boolean; unit: OutputSize; count: number };
	decisionMaking: {
		choice?: 'yes' | 'no';
		decisionsSimple: number;
		decisionsComplex: number;
	};
	outputSize?: OutputSize;
	/** Number of output units of `outputSize` (default 1). */
	outputSizeCount: number;
	thinkingTokensPerTurn: number;
	modelId?: string;
	pricingTier: PricingTier;
	cachedInputRatio: number;
	/** Output display currency (default GBP). */
	currency: Currency;
	/** Inputs grouped per model request when interaction type is batch (default 1). */
	bulkSize: number;
	/** Assistive only: average back-and-forth turns per conversation (default 1). */
	turnsPerConversation: number;
	/** Agent type for runtime cost estimation (drives vCPU/RAM lookup). */
	agentType: AgentType;
	/** Whether to include infrastructure runtime cost in the results TCO. */
	enableRuntime: boolean;
}

export const initialWizardState: WizardState = {
	stepIndex: 0,
	contentTypes: ['text'],
	interactionType: 'assistive',
	// Uses the default calendar basis (365/12 days/month, 365 days/year).
	volumetrics: deriveVolumetrics('perDay', 100),
	dataUnits: {
		textPages: 1,
		voiceMinutes: 1,
		imageCount: 1,
		imageSize: 'large',
		videoMinutes: 1,
	},
	contextDocuments: [],
	additionalContext: { enabled: false, unit: 'page', count: 1 },
	decisionMaking: { choice: 'no', decisionsSimple: 1, decisionsComplex: 0 },
	outputSize: 'page',
	outputSizeCount: 1,
	thinkingTokensPerTurn: 0,
	modelId: undefined,
	pricingTier: 'standard',
	cachedInputRatio: 0,
	currency: 'GBP',
	bulkSize: 1,
	turnsPerConversation: 1,
	agentType: 'none',
	enableRuntime: false,
};

/**
 * Build the EstimateInput payload from wizard state. Only includes dataUnits
 * for selected content types (Req 4.5) and decision fields when enabled.
 */
export function toEstimateInput(state: WizardState): EstimateInput {
	const dataUnits: EstimateInput['dataUnits'] = {};
	if (state.contentTypes.includes('text')) dataUnits.textPages = state.dataUnits.textPages;
	if (state.contentTypes.includes('voice')) dataUnits.voiceMinutes = state.dataUnits.voiceMinutes;
	if (state.contentTypes.includes('images')) {
		dataUnits.imageCount = state.dataUnits.imageCount;
		dataUnits.imageSize = state.dataUnits.imageSize;
	}
	if (state.contentTypes.includes('video')) dataUnits.videoMinutes = state.dataUnits.videoMinutes;

	const enabled = state.decisionMaking.choice === 'yes';

	return {
		contentTypes: state.contentTypes,
		interactionType: state.interactionType as InteractionType,
		volumetrics: state.volumetrics,
		dataUnits,
		contextDocuments: state.contextDocuments,
		additionalContext: state.additionalContext.enabled
			? { enabled: true, unit: state.additionalContext.unit, count: state.additionalContext.count }
			: { enabled: false },
		decisionMaking: {
			enabled,
			...(enabled
				? {
						decisionsSimple: state.decisionMaking.decisionsSimple,
						decisionsComplex: state.decisionMaking.decisionsComplex,
					}
				: {}),
		},
		outputSize: state.outputSize as OutputSize,
		outputSizeCount: state.outputSizeCount,
		thinkingTokensPerTurn: state.thinkingTokensPerTurn,
		modelId: state.modelId as string,
		pricingTier: state.pricingTier,
		cachedInputRatio: state.cachedInputRatio,
		currency: state.currency,
		bulkSize: state.bulkSize,
		turnsPerConversation: state.turnsPerConversation,
	};
}
